import { useCallback, useMemo } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import {
  connectionRows,
  CreateIssueTypeDocument,
  type CreateIssueTypeInput,
  CustomFieldsListDataDocument,
  DeleteIssueTypeDocument,
  DuplicateIssueTypeDocument,
  GetIssueTypeDocument,
  GetIssueTypesDocument,
  IssueTypeListDocument,
  type IssueTypeFieldCustomField,
  type IssueTypeFieldRow,
  type IssueTypeSettingsRow,
  type IssueTypeWithFields,
  ReorderIssueTypeFieldDocument,
  SetDefaultIssueTypeDocument,
  UpdateIssueTypeDocument,
  type UpdateIssueTypeInput,
} from "#app/lib/data";
import {
  addIssueTypeToCache,
  removeIssueTypeFromCache,
  writeIssueTypeLayout,
} from "./issue-type-cache-utils";
import { toastMutationError, resultMessage } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, SettingsEvent, trackEvent } from "#app/lib/analytics";

const trackIssueType = (
  event: (typeof SettingsEvent)[keyof typeof SettingsEvent],
  data?: Record<string, unknown>,
) => trackEvent({ context: AnalyticsContext.Settings, event, label: "issue_type", ...(data ? { data } : {}) });

// The issue-type fragments under the names this feature reads them by. The
// schema already composes them by fragment spread, so these are aliases.
export type CustomFieldItem = IssueTypeFieldCustomField;
export type IssueTypeFieldItem = IssueTypeFieldRow;
export type IssueTypeItem = IssueTypeSettingsRow;
export type IssueTypeDetail = IssueTypeWithFields;

const ISSUE_TYPES_QUERY_VARS = { limit: 100 };
const CUSTOM_FIELDS_QUERY_VARS = { limit: 200, orderBy: { name: 1 } };

// Default flips touch two rows (the new default and whichever type held the
// flag before), so refetch both list queries — the settings list AND the
// workspace-data copy the create form's preselect reads.
const DEFAULT_FLIP_REFETCHES = [
  { query: GetIssueTypesDocument, variables: ISSUE_TYPES_QUERY_VARS },
  { query: IssueTypeListDocument },
];

export function useIssueTypes() {
  const { data, loading, error, refetch } = useQuery(GetIssueTypesDocument, {
    variables: ISSUE_TYPES_QUERY_VARS,
    fetchPolicy: "cache-and-network",
  });

  const connection = data?.issueTypes;
  const issueTypes: IssueTypeItem[] = useMemo(
    () => connectionRows(connection),
    [connection],
  );

  return {
    issueTypes,
    total: connection?.total ?? 0,
    isLoading: loading,
    error,
    refetch,
  };
}

export function useIssueTypeMutations() {
  const apolloClient = useApolloClient();
  const [createMutation] = useMutation(CreateIssueTypeDocument);
  const [updateMutation] = useMutation(UpdateIssueTypeDocument);
  const [deleteMutation] = useMutation(DeleteIssueTypeDocument);
  const [duplicateMutation] = useMutation(DuplicateIssueTypeDocument);
  const [setDefaultMutation] = useMutation(SetDefaultIssueTypeDocument);
  const [reorderFieldMutation] = useMutation(ReorderIssueTypeFieldDocument);

  const createIssueType = useCallback(
    async (input: CreateIssueTypeInput) => {
      try {
        const { data } = await createMutation({
          variables: { input },
          update(cache, { data }) {
            const result = data?.createIssueType;
            if (result?.__typename !== "IssueType") return;
            addIssueTypeToCache(cache, result);
          },
        });

        const result = data?.createIssueType;
        if (result?.__typename === "IssueType") {
          trackIssueType(SettingsEvent.Create);
          return result as IssueTypeItem;
        }
        toastManager.add({ title: resultMessage(result) ?? "Failed to create Project type", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to create Project type");
        return null;
      }
    },
    [createMutation],
  );

  const updateIssueType = useCallback(
    // `optimistic` writes the layout lists into the cache before the request,
    // so a dropped row stays where it landed, and restores them on failure.
    async (
      id: string,
      input: UpdateIssueTypeInput,
      options?: { optimistic?: boolean },
    ) => {
      const rollback = options?.optimistic
        ? writeIssueTypeLayout(apolloClient.cache, id, input)
        : null;
      try {
        const { data } = await updateMutation({
          variables: { id, input },
        });

        const result = data?.updateIssueType;
        if (result?.__typename === "IssueType") {
          trackIssueType(SettingsEvent.Update, { fields: Object.keys(input) });
          return result as IssueTypeItem;
        }
        rollback?.();
        toastManager.add({ title: resultMessage(result) ?? "Failed to update Project type", type: "error" });
        return null;
      } catch (err) {
        rollback?.();
        toastMutationError(err, "Failed to update Project type");
        return null;
      }
    },
    [apolloClient, updateMutation],
  );

  const deleteIssueType = useCallback(
    async (id: string) => {
      try {
        const { data } = await deleteMutation({
          variables: { id },
          update(cache, { data }) {
            const result = data?.deleteIssueType;
            if (result?.__typename !== "IssueType") return;
            removeIssueTypeFromCache(cache, result.issueTypeId);
            const cacheId = cache.identify(result);
            // `identify` answers `undefined` for an object it cannot key, and
            // the cache reads an absent `id` as the ROOT — the eviction would
            // drop a ROOT_QUERY field instead of this entity.
            if (cacheId) cache.evict({ id: cacheId });
            cache.gc();
          },
        });
        const result = data?.deleteIssueType;
        if (result?.__typename === "IssueType") {
          trackIssueType(SettingsEvent.Delete);
          return;
        }
        // Typed rejections (BUILT_IN_UNDELETABLE and friends) carry
        // user-readable copy — surface it directly.
        toastManager.add({
          title:
            (result && "message" in result && result.message) ||
            "Failed to delete Project type",
          type: "error",
        });
      } catch (err) {
        toastMutationError(err, "Failed to delete Project type");
      }
    },
    [deleteMutation],
  );

  // Copies the source type (name + " (copy)", description, icon, field
  // bindings) into a fresh type. Returns the copy so callers can navigate
  // to it.
  const duplicateIssueType = useCallback(
    async (id: string) => {
      try {
        const { data } = await duplicateMutation({
          variables: { id },
          update(cache, { data }) {
            const result = data?.duplicateIssueType;
            if (!result) return;
            addIssueTypeToCache(cache, result);
          },
        });
        const result = data?.duplicateIssueType;
        if (result) {
          trackIssueType(SettingsEvent.Duplicate);
          return result;
        }
        toastManager.add({ title: "Failed to duplicate Project type", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to duplicate Project type");
        return null;
      }
    },
    [duplicateMutation],
  );

  const setDefaultIssueType = useCallback(
    async (id: string) => {
      try {
        await setDefaultMutation({
          variables: { id },
          refetchQueries: DEFAULT_FLIP_REFETCHES,
        });
        trackIssueType(SettingsEvent.SetDefault);
      } catch (err) {
        toastMutationError(err, "Failed to update default Project type");
      }
    },
    [setDefaultMutation],
  );

  const reorderIssueTypeField = useCallback(
    async (issueTypeId: string, customFieldId: string, sortOrder: number) => {
      await reorderFieldMutation({
        variables: { issueTypeId, customFieldId, sortOrder },
      });
      trackEvent({
        context: AnalyticsContext.Settings,
        event: SettingsEvent.Reorder,
        label: "issue_type_field",
      });
    },
    [reorderFieldMutation],
  );

  // Optimistically updates a single junction row's sortOrder in the Apollo
  // cache and re-sorts the issue type's fields array, so the row repositions
  // before the network round-trip completes. Mirrors the stages pattern in
  // `use-stages-list.ts`.
  const optimisticReorderField = useCallback(
    (issueTypeId: string, customFieldId: string, sortOrder: number) => {
      // `identify` answers `undefined` for an object it cannot key, and the
      // cache reads an absent `id` as the ROOT — the edit would land on
      // ROOT_QUERY.
      const cacheId = apolloClient.cache.identify({
        __typename: "IssueType",
        issueTypeId,
      });
      if (!cacheId) return;

      apolloClient.cache.modify({
        id: cacheId,
        fields: {
          fields(existing: readonly any[] = []) {
            return [...existing]
              .map((row) =>
                row.customFieldId === customFieldId
                  ? { ...row, sortOrder }
                  : row,
              )
              .sort(
                (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
              );
          },
        },
      });
    },
    [apolloClient],
  );

  return {
    createIssueType,
    updateIssueType,
    deleteIssueType,
    duplicateIssueType,
    setDefaultIssueType,
    reorderIssueTypeField,
    optimisticReorderField,
  };
}

export function useIssueType(id: string) {
  const variables = useMemo(() => ({ id }), [id]);
  const { data, loading, error, refetch } = useQuery(GetIssueTypeDocument, {
    variables,
    fetchPolicy: "cache-and-network",
    skip: !id,
  });

  // apiv2 returns `fields` as nullable; the IssueTypeDetail shape expects
  // an array. Coerce missing → []. Wider compatibility shim until callers
  // switch to a nullable shape.
  const issueType: IssueTypeDetail | null = data?.issueType
    ? { ...data.issueType, fields: data.issueType.fields ?? [] }
    : null;

  return { issueType, isLoading: loading, error, refetch };
}

export function useCustomFields() {
  const { data, loading, error } = useQuery(CustomFieldsListDataDocument, {
    variables: CUSTOM_FIELDS_QUERY_VARS,
    fetchPolicy: "cache-and-network",
  });

  const connection = data?.customFields;
  const customFields: CustomFieldItem[] = useMemo(
    () => connectionRows(connection),
    [connection],
  );

  return { customFields, isLoading: loading, error };
}
